import type { ThemeMode } from '@/contexts/ThemeContext'

import { getCssVarRgb } from './theme-color'

const DARK_AXIS_LINE_OPACITY = 0.18
const DARK_X_GRID_LINE_OPACITY = 0.1
const DARK_Y_GRID_LINE_OPACITY = 0.3
const DARK_DUAL_AXES_GRID_LINE_OPACITY = 0.18

interface LineChartAxisThemeOptions {
  gridColor?: string
  lightXAxisGridOpacity?: number
  lightYAxisGridOpacity?: number
  showGridInLight?: boolean
  showAxisLineInLight?: boolean
}

function createAxisLine(gridColor: string, opacity: number) {
  return {
    style: { lineWidth: 1, stroke: gridColor, opacity },
  }
}

function createGridLine(gridColor: string, opacity: number) {
  return {
    line: {
      type: 'line',
      style: { lineWidth: 1, color: gridColor, opacity },
    },
  }
}

export function getChartThemeColors() {
  return {
    grid: getCssVarRgb('--color-neutral-200', '#EEEFF1'),
    crosshair: getCssVarRgb('--color-neutral-400', '#B4B6BE'),
    highlight: getCssVarRgb('--color-highlight', '#365FEF'),
    warning: getCssVarRgb('--color-warning', '#FF8447'),
    axisText: getCssVarRgb('--color-text-tertiary', '#999999'),
    legendText: getCssVarRgb('--color-text-secondary', '#535862'),
  }
}

export function getLineChartAxisTheme(
  themeMode: ThemeMode,
  isEmpty: boolean,
  {
    gridColor = getChartThemeColors().grid,
    lightXAxisGridOpacity = DARK_X_GRID_LINE_OPACITY,
    lightYAxisGridOpacity = DARK_Y_GRID_LINE_OPACITY,
    showGridInLight = true,
    showAxisLineInLight = false,
  }: LineChartAxisThemeOptions = {},
) {
  const isDark = themeMode === 'dark'
  const showAxisLine = isDark || showAxisLineInLight
  const showGrid = isDark || showGridInLight

  return {
    axisLine: showAxisLine
      ? createAxisLine(gridColor, isDark ? DARK_AXIS_LINE_OPACITY : 1)
      : undefined,
    xGrid: isEmpty
      ? null
      : showGrid
        ? createGridLine(gridColor, isDark ? DARK_X_GRID_LINE_OPACITY : lightXAxisGridOpacity)
        : undefined,
    yGrid: isEmpty
      ? null
      : showGrid
        ? createGridLine(gridColor, isDark ? DARK_Y_GRID_LINE_OPACITY : lightYAxisGridOpacity)
        : undefined,
  }
}

export function getDarkDualAxesTheme(themeMode: ThemeMode, gridColor = getChartThemeColors().grid) {
  if (themeMode !== 'dark') {
    return {
      axisLine: undefined,
      grid: undefined,
    }
  }

  return {
    axisLine: createAxisLine(gridColor, DARK_AXIS_LINE_OPACITY),
    grid: createGridLine(gridColor, DARK_DUAL_AXES_GRID_LINE_OPACITY),
  }
}
